import React, { useState } from "react";
import useCommunityStats from "../hooks/useCommunityStats";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  Card,
  CardContent,
  Chip,
  Paper,
  alpha,
  useTheme,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import CreditScoreRoundedIcon from "@mui/icons-material/CreditScoreRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import HandshakeRoundedIcon from "@mui/icons-material/HandshakeRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import StarsRoundedIcon from "@mui/icons-material/StarsRounded";
import HealthAndSafetyRoundedIcon from "@mui/icons-material/HealthAndSafetyRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import ProtectedCertificateModal from "../components/ProtectedCertificateModal";

export default function Landing() {
  const communityStats = useCommunityStats();
  const currentYear = new Date().getFullYear();
  const yearsOfService = currentYear - 2007;
  const theme = useTheme();
  const [copiedAccount, setCopiedAccount] = useState("");
  const [certOpen, setCertOpen] = useState(false);

  const handleCopy = (acctNumber) => {
    navigator.clipboard.writeText(acctNumber);
    setCopiedAccount(acctNumber);
    setTimeout(() => setCopiedAccount(""), 2500);
  };

  const portfolios = [
    {
      title: "Shares Capital",
      desc: "Member equity ownership and annual cooperative dividend distribution based on collective surplus.",
      icon: <StarsRoundedIcon sx={{ fontSize: 28, color: "#9333EA" }} />,
      color: "#9333EA",
    },
    {
      title: "Regular Savings",
      desc: "Flexible, disciplined monthly savings ledger maintained with non-interest Islamic principles without predatory deductions.",
      icon: <SavingsRoundedIcon sx={{ fontSize: 28, color: "#16A34A" }} />,
      color: "#16A34A",
    },
    {
      title: "Non-Interest Loans",
      desc: "Ethical non-interest loans designed for personal advancement, education, and family development.",
      icon: <CreditScoreRoundedIcon sx={{ fontSize: 28, color: "#EA580C" }} />,
      color: "#EA580C",
    },
    {
      title: "Business Procurement",
      desc: "Direct capital deployment and goods purchase partnerships for small enterprises and commercial artisans.",
      icon: <StorefrontRoundedIcon sx={{ fontSize: 28, color: "#0D9488" }} />,
      color: "#0D9488",
    },
    {
      title: "Ileya Target Savings",
      desc: "Structured annual target savings enabling members to celebrate festive seasons with dignity and peace of mind.",
      icon: <EventAvailableRoundedIcon sx={{ fontSize: 28, color: "#D97706" }} />,
      color: "#D97706",
    },
    {
      title: "Welfare & Relief Fund",
      desc: "A cooperative social safety cushion extending compassionate assistance during life's critical moments.",
      icon: <HandshakeRoundedIcon sx={{ fontSize: 28, color: "#7C3AED" }} />,
      color: "#7C3AED",
    },
    {
      title: "Takaful Mutual Fund",
      desc: "Shared mutual solidarity reserve protecting the cooperative community against unforeseen adversity.",
      icon: <HealthAndSafetyRoundedIcon sx={{ fontSize: 28, color: "#0E7490" }} />,
      color: "#0E7490",
    },
    {
      title: "Development Fund",
      desc: "Targeted collective investments in society assets, infrastructure, and long-term cooperative ventures.",
      icon: <AccountBalanceRoundedIcon sx={{ fontSize: 28, color: "#0284C7" }} />,
      color: "#0284C7",
    },
    {
      title: "Special Accounts",
      desc: "Tailored individual goal-oriented project savings dedicated to land, asset acquisition, or major ventures.",
      icon: <SecurityRoundedIcon sx={{ fontSize: 28, color: "#4F46E5" }} />,
      color: "#4F46E5",
    },
  ];

  const jaizAccounts = [
    { title: "Main Operations Account", number: "0018169614", category: "Core Operations" },
    { title: "Business Partnership Account", number: "0018169676", category: "Trade & Commerce" },
    { title: "Welfare & Emergency Relief", number: "0018169731", category: "Member Welfare" },
    { title: "Target Savings Account", number: "0018169803", category: "Goal Savings" },
  ];

  return (
    <>
      <Helmet>
        <title>Mutual Trust CMS | Ethical Non-Interest Financial Cooperative</title>
      </Helmet>

      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", color: "text.primary", overflowX: "hidden" }}>
        {/* Navigation Bar */}
        <Box
          component="nav"
          sx={{
            py: 2,
            px: { xs: 2.5, md: 6 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
            position: "sticky",
            top: 0,
            zIndex: 1100,
            boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} component={RouterLink} to="/" sx={{ textDecoration: "none" }}>
            <Box
              component="img"
              src="/assets/icons/mtc.png"
              alt="Mutual Trust Logo"
              sx={{ width: 44, height: 44, objectFit: "contain" }}
            />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "primary.main", lineHeight: 1.1 }}>
                MUTUAL TRUST
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: "0.06em" }}>
                COOPERATIVE MULTIPURPOSE SOCIETY
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              component={RouterLink}
              to="/history"
              variant="text"
              startIcon={<HistoryRoundedIcon />}
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                textTransform: "none",
                display: { xs: "none", sm: "inline-flex" },
                "&:hover": { color: "primary.main" },
              }}
            >
              Our {yearsOfService}-Year Story
            </Button>

            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              endIcon={<LoginRoundedIcon />}
              sx={{
                bgcolor: "primary.main",
                color: "#FFFFFF",
                fontWeight: 700,
                borderRadius: 2,
                px: 2.5,
                py: 1,
                textTransform: "none",
                boxShadow: "0 4px 14px rgba(11, 59, 96, 0.25)",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              }}
            >
              Member Sign In
            </Button>
          </Stack>
        </Box>

        {/* Hero Section */}
        <Box
          sx={{
            position: "relative",
            py: { xs: 8, md: 14 },
            px: { xs: 2.5, md: 6 },
            background: "linear-gradient(150deg, #071C2E 0%, #0B3B60 60%, #054837 100%)",
            color: "#FFFFFF",
            textAlign: "center",
            overflow: "hidden",
          }}
        >
          {/* Subtle Ambient Glows */}
          <Box
            sx={{
              position: "absolute",
              top: "-20%",
              left: "50%",
              transform: "translateX(-50%)",
              width: "800px",
              height: "500px",
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(56, 189, 248, 0.15) 0%, rgba(11, 59, 96, 0) 70%)",
              pointerEvents: "none",
            }}
          />

          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <Box sx={{ mb: 3 }}>
              <Chip
                label="🏛️ Ogun State Reg. No. 7425 • Non-Interest Financial Cooperative • Inception 2007"
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.12)",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  py: 0.5,
                  px: 1,
                }}
              />
            </Box>

            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                lineHeight: { xs: 1.15, md: 1.1 },
                letterSpacing: "-0.03em",
                maxWidth: 960,
                mx: "auto",
                mb: 3,
                fontSize: { xs: "2.125rem", sm: "3rem", md: "3.75rem" },
                color: "#FFFFFF",
              }}
            >
              Ethical Community Banking, Built on Mutual Trust
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                color: "rgba(255, 255, 255, 0.85)",
                maxWidth: 780,
                mx: "auto",
                lineHeight: 1.6,
                mb: 5,
                fontSize: { xs: "1rem", sm: "1.2rem" },
              }}
            >
              Empowering civil servants, academics, artisans, and entrepreneurs across Nigeria through Shariah-compliant non-interest savings, loans, shares, and transparent digital ledgers.
            </Typography>

            {/* Action CTA Buttons */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              sx={{ mb: { xs: 6, md: 8 } }}
            >
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  bgcolor: "#FFFFFF",
                  color: "#0B3B60",
                  fontWeight: 800,
                  px: 3.5,
                  py: 1.75,
                  borderRadius: 2.5,
                  fontSize: "1.05rem",
                  textTransform: "none",
                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)",
                  "&:hover": {
                    bgcolor: "#F8FAFC",
                    transform: "translateY(-2px)",
                    boxShadow: "0 14px 36px rgba(0, 0, 0, 0.35)",
                  },
                }}
              >
                Access Member Portal
              </Button>

              <Button
                component={RouterLink}
                to="/history"
                variant="outlined"
                size="large"
                startIcon={<HistoryRoundedIcon />}
                sx={{
                  color: "#FFFFFF",
                  borderColor: "rgba(255, 255, 255, 0.4)",
                  fontWeight: 700,
                  px: 3,
                  py: 1.75,
                  borderRadius: 2.5,
                  fontSize: "1.05rem",
                  textTransform: "none",
                  backdropFilter: "blur(6px)",
                  "&:hover": {
                    borderColor: "#FFFFFF",
                    bgcolor: "rgba(255, 255, 255, 0.08)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Our {yearsOfService}-Year Journey
              </Button>

              <Button
                variant="outlined"
                size="large"
                startIcon={<VerifiedUserRoundedIcon />}
                onClick={() => setCertOpen(true)}
                sx={{
                  color: "#38BDF8",
                  borderColor: "rgba(56, 189, 248, 0.45)",
                  fontWeight: 700,
                  px: 3,
                  py: 1.75,
                  borderRadius: 2.5,
                  fontSize: "1.05rem",
                  textTransform: "none",
                  backdropFilter: "blur(6px)",
                  bgcolor: "rgba(56, 189, 248, 0.08)",
                  "&:hover": {
                    borderColor: "#38BDF8",
                    bgcolor: "rgba(56, 189, 248, 0.18)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                View Certificate (Reg 7425)
              </Button>
            </Stack>

            {/* Statistics Banner */}
            <Grid container spacing={2} sx={{ maxWidth: 900, mx: "auto" }}>
              {[
                { number: "2007", label: "Year Established", highlight: "#38BDF8" },
                { number: `${communityStats.activeMembers}+`, label: "Diversified Members", highlight: "#34D399" },
                { number: "100%", label: "Non-Interest Framework", highlight: "#FCD34D" },
                { number: "9", label: "Dedicated Financial Portfolios", highlight: "#A78BFA" },
              ].map((stat, idx) => (
                <Grid item xs={6} md={3} key={idx}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: "rgba(255, 255, 255, 0.06)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      backdropFilter: "blur(8px)",
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 900, color: stat.highlight, mb: 0.5 }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.75)", fontWeight: 600 }}>
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* 9 Cooperative Portfolios Section */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Chip
              label="WHAT WE OFFER"
              color="primary"
              size="small"
              sx={{ fontWeight: 800, letterSpacing: "0.08em", mb: 1.5 }}
            />
            <Typography variant="h3" sx={{ fontWeight: 800, color: "text.primary", mb: 2, letterSpacing: "-0.02em" }}>
              Simple and Transparent Financial Products
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 680, mx: "auto", fontSize: "1.05rem" }}>
              Explore our non-interest savings, financing, and mutual support schemes—governed democratically and accessible anytime from your portal.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {portfolios.map((item, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: 1,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "all 0.25s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 16px 36px rgba(11, 59, 96, 0.08)",
                      borderColor: item.color,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 2.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: alpha(item.color, 0.08),
                        mb: 2.5,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
                      {item.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Government Accreditation Banner */}
        <Container maxWidth="lg" sx={{ mb: { xs: 8, md: 10 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 3.5,
              border: `1.5px solid ${alpha("#059669", 0.35)}`,
              bgcolor: alpha("#059669", 0.04),
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              justifyContent: "space-between",
              gap: 3,
            }}
          >
            <Stack direction="row" spacing={2.5} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2.5,
                  bgcolor: alpha("#059669", 0.15),
                  color: "#059669",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <VerifiedUserRoundedIcon sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "text.primary" }}>
                    Official Government Accreditation
                  </Typography>
                  <Chip
                    label="REG NO. 7425"
                    size="small"
                    sx={{ bgcolor: alpha("#059669", 0.15), color: "#059669", fontWeight: 800, fontSize: "0.7rem", height: 22 }}
                  />
                </Stack>
                <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 640, lineHeight: 1.6 }}>
                  Mutual Trust Adigbe (Obafemi/Owode) 'FUG' Cooperative Multipurpose Society Limited is officially registered under Section 7(1) of the Co-operative Societies Law, Cap. 26 of Ogun State Laws (1978).
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="contained"
              startIcon={<VerifiedUserRoundedIcon />}
              onClick={() => setCertOpen(true)}
              sx={{
                bgcolor: "#059669",
                color: "#FFFFFF",
                fontWeight: 700,
                textTransform: "none",
                px: 3,
                py: 1.25,
                borderRadius: 2,
                flexShrink: 0,
                boxShadow: "0 4px 14px rgba(5, 150, 105, 0.25)",
                "&:hover": { bgcolor: "#047857" },
              }}
            >
              Verify Certificate
            </Button>
          </Paper>
        </Container>

        {/* Jaiz Bank Operational Accounts Section */}
        <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), py: { xs: 8, md: 10 }, borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider" }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Chip
                label="OFFICIAL BANKING PARTNER"
                color="secondary"
                size="small"
                sx={{ fontWeight: 800, letterSpacing: "0.08em", mb: 1.5 }}
              />
              <Typography variant="h3" sx={{ fontWeight: 800, color: "text.primary", mb: 1.5, letterSpacing: "-0.02em" }}>
                Official Jaiz Bank Accounts
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 620, mx: "auto" }}>
                To make member contributions, savings allocations, or repayments, deposit directly into the appropriate Jaiz Bank account:
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {jaizAccounts.map((acct, idx) => (
                <Grid item xs={12} sm={6} md={3} key={idx}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                      textAlign: "center",
                      position: "relative",
                      transition: "all 0.2s ease",
                      "&:hover": { borderColor: "primary.main", transform: "translateY(-3px)", boxShadow: "0 8px 24px rgba(0,0,0,0.06)" },
                    }}
                  >
                    <Chip
                      label={acct.category}
                      size="small"
                      sx={{ fontSize: "0.7rem", fontWeight: 700, mb: 1.5, bgcolor: alpha(theme.palette.secondary.main, 0.1), color: "secondary.main" }}
                    />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}>
                      {acct.title}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 900,
                        fontFamily: "monospace",
                        color: "primary.main",
                        letterSpacing: "0.05em",
                        my: 1,
                      }}
                    >
                      {acct.number}
                    </Typography>

                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleCopy(acct.number)}
                      startIcon={copiedAccount === acct.number ? <CheckRoundedIcon color="success" /> : <ContentCopyRoundedIcon fontSize="small" />}
                      sx={{
                        mt: 1,
                        textTransform: "none",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        borderColor: copiedAccount === acct.number ? "success.main" : "divider",
                        color: copiedAccount === acct.number ? "success.main" : "text.secondary",
                      }}
                    >
                      {copiedAccount === acct.number ? "Copied!" : "Copy Account"}
                    </Button>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Quranic Foundation Box */}
            <Box
              sx={{
                mt: 6,
                p: 3.5,
                borderRadius: 3,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: alpha("#059669", 0.3),
                maxWidth: 860,
                mx: "auto",
                textAlign: "center",
              }}
            >
              <Typography variant="subtitle2" sx={{ color: "#059669", fontWeight: 800, mb: 1, letterSpacing: "0.04em" }}>
                NON-INTEREST FOUNDATION • SURAH AL-BAQARAH 2:275
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", fontStyle: "italic", lineHeight: 1.7, fontSize: "1rem" }}>
                &ldquo;...Allah has permitted trade and forbidden interest.&rdquo;
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Footer */}
        <Box sx={{ bgcolor: "background.paper", py: 6, px: { xs: 2.5, md: 6 }, borderTop: "1px solid", borderColor: "divider" }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <Box component="img" src="/assets/icons/mtc.png" alt="MTC" sx={{ width: 40, height: 40 }} />
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "primary.main" }}>
                    Mutual Trust Cooperative
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, maxWidth: 440 }}>
                  A premier Multipurpose Society dedicated to ethical non-interest empowerment, transparent financial ledgers, and sustainable community growth.
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocationOnRoundedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Permanent Site, Odofin Via Somorin Obantoko, Abeokuta, Ogun State
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneInTalkRoundedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      Secretariat Phone: 08034660713
                    </Typography>
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6} sx={{ textAlign: { xs: "left", md: "right" } }}>
                <Stack direction="row" spacing={2} justifyContent={{ xs: "flex-start", md: "flex-end" }} sx={{ mb: 2 }}>
                  <Button component={RouterLink} to="/history" variant="text" sx={{ color: "text.secondary", textTransform: "none", fontWeight: 600 }}>
                    Our History
                  </Button>
                  <Button component={RouterLink} to="/login" variant="text" sx={{ color: "primary.main", textTransform: "none", fontWeight: 700 }}>
                    Portal Login
                  </Button>
                </Stack>
                <Typography variant="caption" sx={{ color: "text.disabled", display: "block" }}>
                  © 2007 — {new Date().getFullYear()} Mutual Trust Cooperative Multipurpose Society. All rights reserved.
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <ProtectedCertificateModal open={certOpen} onClose={() => setCertOpen(false)} />
      </Box>
    </>
  );
}
